import { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { isSupportedLocale, type SectionPermission, type SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { grantedSections, landingFor, type AccountActions, type AccountState } from './account';
import { InvitePanel, LandingPanel } from './AccountPanels';
import { AssessmentsPanel } from './AssessmentsPanel';
import { AthletesPanel } from './AthletesPanel';
import { BillingPanel } from './BillingPanel';
import { CalendarPanel } from './CalendarPanel';
import { CompetitionsPanel } from './CompetitionsPanel';
import { GuardianLinksPanel } from './GuardianLinksPanel';
import { ChangeRequestsPanel } from './ChangeRequestsPanel';
import { ChildRequestSection } from './ChildRequestSection';
import { ChildRequestsPanel } from './ChildRequestsPanel';
import { ImportsPanel } from './ImportsPanel';
import { ClubDataPanel } from './ClubDataPanel';
import { LogoPanel } from './LogoPanel';
import { PaymentsPanel } from './PaymentsPanel';
import { InvitationsPanel } from './InvitationsPanel';
import { InviteParentPanel } from './InviteParentPanel';
import { RolesPanel } from './RolesPanel';
import { SecretaryDashboard } from './SecretaryDashboard';
import { SessionPolicyPanel } from './SessionPolicyPanel';
import { MailSettingsPanel } from './MailSettingsPanel';
import { TeamsPanel } from './TeamsPanel';
import { TrainingPanel } from './TrainingPanel';
import { AppFrame } from './AppFrame';
import { HomePanel } from './HomePanel';
import { isFamilyView, navigationLabel, sectionIntroduction } from './navigation';
import { readPendingInvitation } from './pendingInvitation';
import { TaskSection } from './TaskSection';
import { hasFeature, isAccessAllowed, routes, visibleNavigation } from './routes';

const localeStorageKey = 'stm.locale';

/** Error codes the API appends to `/login` after a failed sign-in; unknown codes show no message. */
const loginErrorKeys: Readonly<Record<string, TranslationKey>> = {
  login_failed: 'login.error.loginFailed',
  provider_unavailable: 'login.error.providerUnavailable',
};

/** Browser storage can be missing or blocked, so a failure falls back to the default locale. */
function storedLocale(): SupportedLocale {
  try {
    const value = localStorage.getItem(localeStorageKey);
    return isSupportedLocale(value) ? value : 'ro';
  } catch {
    return 'ro';
  }
}

function rememberLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(localeStorageKey, locale);
  } catch {
    // The choice still applies for this session when storage is unavailable.
  }
}

const noAccountActions: AccountActions = {
  selectOrganization: () => Promise.resolve(),
  acceptInvitation: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  refreshAccount: () => Promise.resolve(),
  rememberLocale: () => undefined,
};

export interface ShellProps {
  initialLocale?: SupportedLocale;
  /** Who is signed in; the shell shows only public areas until `/me` has answered. */
  account?: AccountState;
  actions?: AccountActions;
  /** Overrides the sections derived from `account`; tests use it to preview a role. */
  grantedPermissions?: ReadonlySet<SectionPermission>;
}

export function Shell({
  initialLocale,
  account = { status: 'anonymous' },
  actions = noAccountActions,
  grantedPermissions = grantedSections(account),
}: ShellProps): React.JSX.Element {
  const [locale, setLocale] = useState<SupportedLocale>(initialLocale ?? storedLocale);
  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = account.status === 'signedIn' ? account : null;
  const activeOrganizationId = signedIn?.me.activeOrganizationId ?? undefined;
  const navigation = visibleNavigation(grantedPermissions, activeOrganizationId);
  const activeRoute = routes.find((route) => {
    const match = matchPath({ path: route.pattern, end: true }, location.pathname);
    if (match === null || !isAccessAllowed(route.access, grantedPermissions)) return false;
    // A path naming another organization is not the active one: show the generic not-found.
    const pathOrganization = match.params.organizationId;
    return (
      pathOrganization === undefined ||
      signedIn === null ||
      pathOrganization === activeOrganizationId
    );
  });
  const isLandingPage = location.pathname === '/';
  const landing = landingFor(account);
  const isLoginPage = activeRoute?.pattern === '/login';
  const isInvitePage = activeRoute?.pattern === '/invite';
  const returnTo = new URLSearchParams(location.search).get('returnTo');
  const loginErrorCode = new URLSearchParams(location.search).get('error');
  const loginErrorKey = loginErrorCode === null ? undefined : loginErrorKeys[loginErrorCode];
  // Only a section the person may open but that has no working page yet gets the "not enabled" notice.
  const isFeaturePending =
    !isLandingPage &&
    activeRoute !== undefined &&
    activeRoute.access !== 'public' &&
    !hasFeature(activeRoute.pattern, signedIn?.me.permissions ?? []);
  const titleKey = isLandingPage ? 'page.landing' : (activeRoute?.titleKey ?? 'page.notFound');
  const helpKey = isLandingPage ? 'help.landing' : (activeRoute?.helpKey ?? 'help.notFound');
  const familyView = isFamilyView(signedIn?.me.permissions ?? []);
  const currentNavigation = navigation.find((item) => item.permission === activeRoute?.access);
  const pageTitleKey =
    currentNavigation === undefined ? titleKey : navigationLabel(currentNavigation, familyView);
  const introductionKey: TranslationKey = isLandingPage
    ? 'intro.landing'
    : activeRoute?.access !== undefined && activeRoute.access !== 'public'
      ? sectionIntroduction(activeRoute.access, familyView)
      : isLoginPage
        ? 'intro.login'
        : isInvitePage
          ? 'intro.invite'
          : 'intro.notFound';

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = translate(locale, 'app.name');
    rememberLocale(locale);
  }, [locale]);

  // The language chosen for the active organization applies when the account loads or switches.
  const accountLocale = signedIn?.me.locale ?? null;
  useEffect(() => {
    if (isSupportedLocale(accountLocale)) setLocale(accountLocale);
  }, [accountLocale, activeOrganizationId]);

  // Navigation that depends on who is signed in. Anonymous visitors never learn which paths exist:
  // everything but the public pages leads to sign-in, and signed-in visitors skip it. A sign-in that
  // lost its way back (an expired attempt, a password reset in another tab) still ends at the
  // invitation the person opened before signing in.
  useEffect(() => {
    const isPublicPage = isLoginPage || isInvitePage;
    if (account.status === 'anonymous' && !isPublicPage) {
      const target = isLandingPage
        ? '/login'
        : `/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`;
      void navigate(target, { replace: true });
    } else if (
      signedIn !== null &&
      (isLandingPage || isLoginPage) &&
      readPendingInvitation() !== ''
    ) {
      void navigate('/invite', { replace: true });
    } else if (landing.kind === 'home' && (isLandingPage || isLoginPage)) {
      void navigate(`/app/${landing.organizationId}/home`, { replace: true });
    } else if (signedIn !== null && isLoginPage) {
      void navigate('/', { replace: true });
    }
  }, [
    account.status,
    isInvitePage,
    isLandingPage,
    isLoginPage,
    landing,
    location,
    navigate,
    signedIn,
  ]);

  const activeMembership = signedIn?.me.memberships.find(
    (membership) => membership.organizationId === activeOrganizationId,
  );

  return (
    <AppFrame
      locale={locale}
      onLocaleChange={setLocale}
      account={account}
      actions={actions}
      navigation={navigation}
    >
      <Box component="header" sx={{ mb: 3 }}>
        {activeMembership !== undefined && (
          <Typography variant="overline" color="text.secondary">
            {activeMembership.organizationName}
          </Typography>
        )}
        <Typography variant="h4" component="h1" gutterBottom>
          {translate(locale, pageTitleKey)}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
          {translate(locale, introductionKey)}
        </Typography>
      </Box>

      {isLoginPage && (
        <Box sx={{ mb: 3 }}>
          {loginErrorKey !== undefined && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {translate(locale, loginErrorKey)}
            </Alert>
          )}
          <Button
            variant="contained"
            component="a"
            href={`/api/v1/auth/login?locale=${locale}${
              returnTo === null ? '' : `&returnTo=${encodeURIComponent(returnTo)}`
            }`}
          >
            {translate(locale, 'login.signIn')}
          </Button>
        </Box>
      )}
      {signedIn?.me.mfaRequired === true && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate(locale, 'error.mfaRequired')}
        </Alert>
      )}
      {isInvitePage && <InvitePanel locale={locale} account={account} actions={actions} />}
      {isLandingPage && (
        <LandingPanel locale={locale} landing={landing} account={account} actions={actions} />
      )}
      {activeRoute?.pattern === '/app/:organizationId/home' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('imports.manage') && (
          <SecretaryDashboard locale={locale} organizationId={activeOrganizationId} />
        )}
      {activeRoute?.pattern === '/app/:organizationId/home' && signedIn !== null && (
        <HomePanel locale={locale} navigation={navigation} permissions={signedIn.me.permissions} />
      )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <AthletesPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            canManage={signedIn.me.permissions.includes('athletes.manage')}
            canViewMemberships={
              signedIn.me.permissions.includes('athletes.browse') ||
              signedIn.me.permissions.includes('children.view')
            }
            canRequestChanges={signedIn.me.permissions.includes('children.view')}
            canRecordPayments={signedIn.me.permissions.includes('billing.manage')}
          />
        )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('children.view') && (
          <TaskSection
            locale={locale}
            titleKey="childRequests.title"
            descriptionKey="task.childRequests.description"
            defaultOpen
          >
            <ChildRequestSection
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('invitations.manage') && (
          <TaskSection
            locale={locale}
            titleKey="inviteParent.title"
            descriptionKey="task.inviteParent.description"
          >
            <InviteParentPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('guardians.verify') && (
          <TaskSection
            locale={locale}
            titleKey="childRequests.review.title"
            descriptionKey="task.childRequests.review.description"
          >
            <ChildRequestsPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('guardians.verify') && (
          <TaskSection
            locale={locale}
            titleKey="task.guardians"
            descriptionKey="task.guardians.description"
          >
            <GuardianLinksPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('athletes.manage') && (
          <TaskSection
            locale={locale}
            titleKey="task.changes"
            descriptionKey="task.changes.description"
          >
            <ChangeRequestsPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/athletes' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('imports.manage') && (
          <TaskSection
            locale={locale}
            titleKey="imports.title"
            descriptionKey="task.imports.description"
          >
            <ImportsPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/teams' &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <TeamsPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            canPlaceAthletes={signedIn.me.permissions.includes('athletes.browse')}
          />
        )}
      {(activeRoute?.pattern === '/calendar' || activeRoute?.pattern === '/events/:eventId') &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <CalendarPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            permissions={signedIn.me.permissions}
            initialEventId={
              matchPath({ path: '/events/:eventId', end: true }, location.pathname)?.params.eventId
            }
          />
        )}
      {activeRoute?.pattern === '/training' &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <TrainingPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            canManage={signedIn.me.permissions.includes('training.manage')}
          />
        )}
      {(activeRoute?.pattern === '/assessments' ||
        activeRoute?.pattern === '/assessments/:assessmentId') &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <AssessmentsPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            permissions={signedIn.me.permissions}
            initialSessionId={
              matchPath({ path: '/assessments/:assessmentId', end: true }, location.pathname)
                ?.params.assessmentId
            }
          />
        )}
      {(activeRoute?.pattern === '/billing' ||
        activeRoute?.pattern === '/billing/accounts/:accountId') &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('billing.manage') && (
          <BillingPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            initialAccountId={
              matchPath({ path: '/billing/accounts/:accountId', end: true }, location.pathname)
                ?.params.accountId
            }
          />
        )}
      {activeRoute?.pattern === '/payments' &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <PaymentsPanel locale={locale} organizationId={activeOrganizationId} />
        )}
      {activeRoute?.pattern === '/competitions' &&
        signedIn !== null &&
        activeOrganizationId !== undefined && (
          <CompetitionsPanel
            locale={locale}
            organizationId={activeOrganizationId}
            csrfToken={signedIn.csrfToken}
            canCreate={signedIn.me.permissions.includes('competitions.manage')}
            canManageRules={signedIn.me.permissions.includes('rules.manage')}
            canListSeasons={signedIn.me.permissions.includes('teams.view')}
            canViewRegistrations={signedIn.me.permissions.includes('athletes.browse')}
          />
        )}
      {activeRoute?.pattern === '/settings' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('organization.configure') && (
          <TaskSection
            locale={locale}
            titleKey="clubData.title"
            descriptionKey="task.clubData.description"
          >
            <ClubDataPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
              refreshAccount={actions.refreshAccount}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/settings' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('roles.manage') && (
          <TaskSection
            locale={locale}
            titleKey="roles.title"
            descriptionKey="task.access.description"
          >
            <RolesPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
              refreshAccount={actions.refreshAccount}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/settings' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('organization.configure') && (
          <TaskSection locale={locale} titleKey="logo.title">
            <LogoPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
              logoVersion={signedIn.me.logoVersion}
              refreshAccount={actions.refreshAccount}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/settings' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('organization.configure') && (
          <TaskSection locale={locale} titleKey="sessionPolicy.title">
            <SessionPolicyPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/settings' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('organization.configure') && (
          <TaskSection locale={locale} titleKey="mailSettings.title">
            <MailSettingsPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
            />
          </TaskSection>
        )}
      {activeRoute?.pattern === '/settings' &&
        signedIn !== null &&
        activeOrganizationId !== undefined &&
        signedIn.me.permissions.includes('invitations.manage') && (
          <TaskSection
            locale={locale}
            titleKey="invitations.title"
            descriptionKey="task.invitations.description"
          >
            <InvitationsPanel
              locale={locale}
              organizationId={activeOrganizationId}
              csrfToken={signedIn.csrfToken}
              canOfferStaffRoles={signedIn.me.permissions.includes('roles.manage')}
            />
          </TaskSection>
        )}
      {isFeaturePending && (
        <>
          <Typography variant="h6" component="h2" gutterBottom>
            {translate(locale, 'shell.foundationTitle')}
          </Typography>
          <Typography>{translate(locale, 'shell.foundationBody')}</Typography>
          <Typography sx={{ mt: 2 }}>{translate(locale, 'shell.placeholder')}</Typography>
        </>
      )}
      <TaskSection
        key={location.pathname}
        locale={locale}
        titleKey="pageGuide.title"
        descriptionKey="pageGuide.subtitle"
        defaultOpen={activeRoute?.access === 'public' || activeRoute === undefined}
      >
        <Typography sx={{ maxWidth: 900, whiteSpace: 'pre-line' }}>
          {translate(locale, helpKey)}
        </Typography>
        <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
          {translate(locale, 'shell.help')}
        </Typography>
      </TaskSection>
    </AppFrame>
  );
}
